import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity, Platform, Linking } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { Paperclip, LinkSimple } from "@phosphor-icons/react";
import { ProjectSubmission } from "../../../graphql/generated/graphql";
import useFileFetch from "../../../hooks/useFileFetch";

interface ProjectGradedSectionProps {
  submission: ProjectSubmission;
}

const getGradeStyle = (grade?: number | null) => {
  if (grade === undefined || grade === null) {
    return { backgroundColor: "transparent" };
  }

  if (grade >= 80) {
    return { backgroundColor: "#4BA30D" };
  } else if (grade >= 60) {
    return { backgroundColor: "#3B82F6" };
  } else if (grade >= 40) {
    return { backgroundColor: "#F59E0B" };
  } else {
    return { backgroundColor: "#DC2626" };
  }
};

const ProjectGradedSection: React.FC<ProjectGradedSectionProps> = ({ submission }) => {
  const [showFeedbackDetails, setShowFeedbackDetails] = useState(false);
  const grade = submission?.grade;
  const gradeColors = getGradeStyle(grade);
  const { fetchAndOpenFile } = useFileFetch();

  const handleViewFeedback = () => {
    setShowFeedbackDetails(!showFeedbackDetails);
  };

  const formatReviewDate = (dateString?: string | null) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  return (
    <View style={styles.gradedSection}>
      <View style={styles.gradeRow}>
        <View style={[styles.gradeChip, gradeColors]}>
          <CustomText
            text={grade !== undefined && grade !== null ? `${grade}/100` : "—/100"}
            size="s"
            weight="bold"
            style={styles.gradeText}
            useTranslationText={false}
          />
        </View>
        {submission?.gradedAt && (
          <CustomText
            text={`Reviewed on ${formatReviewDate(submission.gradedAt)}`}
            size="s"
            weight="bold"
            style={styles.reviewDateText}
            useTranslationText={false}
          />
        )}
      </View>
      <CustomSpacing type="vertical" size="s" />
      <CustomText text="Your Project has been graded" size="m" weight="bold" style={styles.gradedTitle} />
      <CustomText
        text="Read your teacher’s feedback below."
        size="s"
        weight="normal"
        style={styles.feedbackDescriptionText}
        useTranslationText={false}
      />
      <CustomSpacing type="vertical" size="s" />
      {!showFeedbackDetails ? (
        <CustomButton
          text="View Feedback"
          size="s"
          styleType="primary"
          onPress={handleViewFeedback}
          style={styles.viewFeedbackButton}
          textStyle={styles.viewFeedbackButtonText}
        />
      ) : (
        <View style={styles.feedbackDetailsContainer}>
          <CustomSpacing type="vertical" size="xl" />
          <CustomText
            text={
              submission?.completedAt
                ? `Submitted at ${new Date(submission.completedAt).toLocaleString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: true,
                  })}`
                : ""
            }
            style={styles.submissionLink}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="s" />
          {(submission?.files?.length > 0 || submission?.submissionLink) && (
            <View style={styles.submissionFilesContainer}>
              {submission?.submissionLink && (
                <TouchableOpacity
                  style={styles.submissionFileItem}
                  onPress={() => {
                    if (Platform.OS === "web") {
                      window.open(submission.submissionLink, "_blank");
                    } else {
                      Linking.openURL(submission.submissionLink);
                    }
                  }}>
                  <LinkSimple size={16} color="#344054" weight="regular" />
                  <CustomText
                    text={submission.submissionLink}
                    style={styles.submissionFileName}
                    useTranslationText={false}
                  />
                </TouchableOpacity>
              )}
              {submission?.files?.map((file) => (
                <TouchableOpacity
                  key={file.id}
                  style={styles.submissionFileItem}
                  onPress={() => fetchAndOpenFile(file.fileURL)}>
                  <Paperclip size={16} color="#344054" weight="regular" />
                  <CustomText
                    text={file.fileName || file.id}
                    style={styles.submissionFileName}
                    useTranslationText={false}
                  />
                </TouchableOpacity>
              ))}
            </View>
          )}
          <CustomSpacing type="vertical" size="m" />
          <CustomText text="Feedback" style={styles.submissionDetailsTitle} size="s" weight="bold" />
          <CustomSpacing type="vertical" size="xs" />
          {(submission as any)?.feedback ? (
            <CustomText text={(submission as any).feedback} style={styles.feedbackText} useTranslationText={false} />
          ) : (
            <CustomText text="Feedback not provided" style={styles.feedbackNotGivenText} />
          )}
          <CustomSpacing type="vertical" size="xl" />
          <CustomButton
            text="Hide Feedback"
            size="s"
            styleType="primary"
            onPress={handleViewFeedback}
            style={styles.viewFeedbackButton}
            textStyle={styles.viewFeedbackButtonText}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  gradedSection: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#EAECF0",
    backgroundColor: "#FFF",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    marginTop: 30,
  },
  gradeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  gradeChip: {
    display: "flex",
    flexDirection: "row",
    paddingVertical: 4,
    paddingHorizontal: 10,
    alignItems: "center",
    gap: 24,
    borderRadius: 12,
    alignSelf: "flex-start",
  },
  gradeText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
  },
  reviewDateText: {
    color: "#45556C",
  },
  gradedTitle: {
    color: "#344054",
  },
  viewFeedbackButton: {
    minWidth: 120,
    alignSelf: "flex-start",
  },
  viewFeedbackButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "700",
  },
  feedbackDetailsContainer: {
    width: "100%",
  },
  submissionDetailsTitle: {
    color: "#45556C",
  },
  submissionLink: {
    fontSize: 14,
    fontWeight: "400",
    color: "#667085",
    lineHeight: 20,
  },
  submissionFilesContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 8,
    width: "100%",
  },
  submissionFileItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: 155.75,
    paddingVertical: 3.395,
    paddingHorizontal: 10,
    justifyContent: "center",
    gap: 5,
    borderRadius: 360,
    backgroundColor: "#F8FAFC",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  submissionFileName: {
    fontSize: 14,
    fontWeight: "400",
    color: "#344054",
    flex: 1,
  },
  submissionNotesText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#667085",
    lineHeight: 20,
  },
  feedbackText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#667085",
    lineHeight: 20,
  },
  feedbackNotGivenText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#667085",
    fontStyle: "italic",
    lineHeight: 20,
  },
  feedbackDescriptionText: {
    color: "#45556C",
  },
});

export default ProjectGradedSection;
